import React, { useState, useEffect, useCallback, useRef } from 'react';
import { taskApi } from '../api/taskApi';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { useAttorneys } from '@/features/matters/hooks/useAttorneys';
import { useMatters } from '@/features/matters/hooks/useMatters';
import type { Task, TaskStatus, TaskPriority, TaskSummary, CreateTaskDto } from '../types/task.types';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';

interface MyTasksPageProps {
  initialTaskId?: string | null;
  onCloseTask?: () => void;
}

const MyTasksPage: React.FC<MyTasksPageProps> = ({ initialTaskId = null, onCloseTask }) => {
  const { appPreferences, hasHydrated } = useUserPreferencesStore();
  const taskPrefs = appPreferences.tasks;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(taskPrefs.defaultStatusFilter);
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newTask, setNewTask] = useState<Partial<CreateTaskDto>>({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assignee_id: '',
  });
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<string | null>(null);
  const { attorneys, loading: loadingUsers } = useAttorneys();
  const { matters, fetchMatters } = useMatters();
  const lastAppliedStatus = useRef<string>(taskPrefs.defaultStatusFilter);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  useEffect(() => {
    if (!hasHydrated) return;
    const preferredStatus = taskPrefs.defaultStatusFilter;
    if (statusFilter === lastAppliedStatus.current && preferredStatus !== statusFilter) {
      setStatusFilter(preferredStatus);
    }
    lastAppliedStatus.current = preferredStatus;
  }, [hasHydrated, statusFilter, taskPrefs.defaultStatusFilter]);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: Record<string, unknown> = {
        page,
        limit: 25,
        sort_by: taskPrefs.defaultSort,
      };
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (priorityFilter !== 'all') filters.priority = priorityFilter;
      if (debouncedSearch) filters.search = debouncedSearch;

      const result = await taskApi.getMyTasks(filters as any);
      const data = result as any;
      setTasks(data.tasks || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, debouncedSearch, page, taskPrefs.defaultSort]);

  const fetchSummary = useCallback(async () => {
    try {
      const result = await taskApi.getSummary();
      const data = result as any;
      setSummary(data.summary || null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchSummary();
  }, [fetchTasks, fetchSummary]);

  // Sync URL-based task ID into local state — only react to initialTaskId changes,
  // NOT selectedTaskId (which would fight the modal close action)
  useEffect(() => {
    if (initialTaskId) {
      setSelectedTaskId(initialTaskId);
    }
  }, [initialTaskId]);

  const handleCreateTask = async () => {
    if (!newTask.title?.trim()) return;
    try {
      setSubmitting(true);
      await taskApi.create(newTask as CreateTaskDto);
      setShowCreateForm(false);
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '', assignee_id: '' });
      await fetchTasks();
      await fetchSummary();
    } catch {
      setError('Failed to create task.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkComplete = async (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    try {
      await taskApi.updateStatus(taskId, 'done');
      await fetchTasks();
      await fetchSummary();
    } catch {
      setError('Failed to update task.');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setDeleteConfirmTaskId(taskId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmTaskId) return;
    try {
      await taskApi.delete(deleteConfirmTaskId);
      setDeleteConfirmTaskId(null);
      await fetchTasks();
      await fetchSummary();
    } catch {
      setError('Failed to delete task.');
      setDeleteConfirmTaskId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'in_progress': return 'bg-lex-accent-soft text-lex-accent';
      case 'review': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'todo': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 dark:text-red-400';
      case 'high': return 'text-orange-600 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-green-600 dark:text-green-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const isOverdue = (dueDate?: string, status?: string) => {
    if (!taskPrefs.showOverdueHighlight) return false;
    if (!dueDate || status === 'done' || status === 'archived') return false;
    return new Date(dueDate) < new Date();
  };

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString() : '';

  // Debounce search input — waits 300ms after user stops typing
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="p-6 max-w-7xl mx-auto space-y-6 lex-tab-panel">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Tasks</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          {showCreateForm ? 'Cancel' : 'New Task'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{summary.total}</div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="text-xs text-yellow-600 dark:text-yellow-400">To Do</div>
            <div className="text-xl font-bold text-yellow-900 dark:text-yellow-100">{summary.by_status?.todo || 0}</div>
          </div>
          <div className="bg-lex-accent-soft p-3 rounded-lg border border-lex-accent">
            <div className="text-xs text-lex-accent">In Progress</div>
            <div className="text-xl font-bold text-lex-accent">{summary.by_status?.in_progress || 0}</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
            <div className="text-xs text-green-600 dark:text-green-400">Done</div>
            <div className="text-xl font-bold text-green-900 dark:text-green-100">{summary.by_status?.done || 0}</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
            <div className="text-xs text-red-600 dark:text-red-400">Overdue</div>
            <div className="text-xl font-bold text-red-900 dark:text-red-100">{summary.overdue}</div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="text-xs text-purple-600 dark:text-purple-400">In Review</div>
            <div className="text-xl font-bold text-purple-900 dark:text-purple-100">{summary.by_status?.review || 0}</div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Create Task</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <input
                type="text"
                value={newTask.title || ''}
                onChange={(e) => setNewTask(p => ({ ...p, title: e.target.value }))}
                placeholder="Task title..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
              <input
                type="date"
                value={newTask.due_date || ''}
                onChange={(e) => setNewTask(p => ({ ...p, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <select
                value={newTask.priority || 'medium'}
                onChange={(e) => setNewTask(p => ({ ...p, priority: e.target.value as TaskPriority }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign To</label>
              <select
                value={newTask.assignee_id || ''}
                onChange={(e) => setNewTask(p => ({ ...p, assignee_id: e.target.value || undefined }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Me (default)</option>
                {attorneys.map(u => (
                  <option key={u.id} value={u.keycloakUserId || u.id}>{u.name} ({u.userType})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matter</label>
              <select
                value={newTask.matter_id || ''}
                onChange={(e) => setNewTask(p => ({ ...p, matter_id: e.target.value || undefined }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">No matter</option>
                {matters.map(m => (
                  <option key={m.id} value={m.id}>{m.matter_number} — {(m as any).title || m.description || ''}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                rows={2}
                value={newTask.description || ''}
                onChange={(e) => setNewTask(p => ({ ...p, description: e.target.value }))}
                placeholder="Description..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(newTask.tags || []).map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-lex-accent-soft text-lex-accent rounded-full">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setNewTask(p => ({ ...p, tags: (p.tags || []).filter(t => t !== tag) }))}
                      className="text-lex-accent hover:text-lex-accent-hover"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Type a tag and press Enter..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val && !(newTask.tags || []).includes(val)) {
                      setNewTask(p => ({ ...p, tags: [...(p.tags || []), val] }));
                      (e.target as HTMLInputElement).value = '';
                    }
                  }
                }}
              />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={handleCreateTask}
              disabled={submitting || !newTask.title?.trim()}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search tasks, descriptions, comments, tags..."
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white flex-1"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Statuses</option>
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="review">In Review</option>
          <option value="done">Done</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Loading tasks...</span>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Task</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 hidden md:table-cell">Matter</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">Subtasks</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">Status</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 hidden md:table-cell">Priority</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 hidden lg:table-cell">Due Date</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 w-40">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {tasks.map((task, idx) => (
                <tr
                  key={task.id}
                  onClick={() => setSelectedTaskId(task.id)}
                  className={`lex-list-row cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    isOverdue(task.due_date, task.status) ? 'bg-red-50/50 dark:bg-red-900/10' : ''
                  }`}
                  style={{ '--row-index': idx } as React.CSSProperties}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {task.title}
                    </div>
                    {task.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{task.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {task.matter ? (
                      <span className="text-xs text-lex-accent font-medium">
                        {task.matter.matter_number}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-center">
                    {(task.subtask_count ?? 0) > 0 ? (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-lex-accent-soft text-lex-accent rounded-full">
                        {task.subtask_count}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                        {task.status.replace(/_/g, ' ')}
                      </span>
                      {task.assignment_status && (
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          task.assignment_status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' :
                          task.assignment_status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                        }`}>
                          {task.assignment_status === 'accepted' ? 'Accepted' : task.assignment_status === 'rejected' ? 'Declined' : 'Pending'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className={isOverdue(task.due_date, task.status) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-gray-400'}>
                      {formatDate(task.due_date) || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {task.status !== 'done' && task.status !== 'archived' && (
                        <button
                          onClick={(e) => handleMarkComplete(e, task.id)}
                          className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/40"
                        >
                          Done
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedTaskId(task.id); }}
                        className="px-2 py-1 text-xs bg-lex-accent-soft text-lex-accent rounded hover:bg-lex-state-hover"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(e, task.id)}
                        className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/40"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {debouncedSearch || statusFilter !== 'all' || priorityFilter !== 'all'
                      ? 'No tasks match your search or filters.'
                      : 'No tasks assigned to you.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={!!selectedTaskId}
        taskId={selectedTaskId}
        onClose={() => {
          setSelectedTaskId(null);
          onCloseTask?.();
        }}
        onUpdated={() => { fetchTasks(); fetchSummary(); }}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmTaskId && (() => {
        const taskToDelete = tasks.find(t => t.id === deleteConfirmTaskId);
        const hasSubtasks = taskToDelete?.subtask_count && taskToDelete.subtask_count > 0;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60" onClick={() => setDeleteConfirmTaskId(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Task</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{taskToDelete?.title}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Are you sure you want to delete this task? This action cannot be undone.
              </p>
              {hasSubtasks && (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                    This task has {taskToDelete.subtask_count} subtask{taskToDelete.subtask_count! > 1 ? 's' : ''}.
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Deleting this task will also remove all subtasks and notify assigned users.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={() => setDeleteConfirmTaskId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete Task
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </div>
  );
};

export default MyTasksPage;
